// @ts-check
import { useDebounceCallback } from '@react-hook/debounce';
import { shouldStopOnboardingRecall } from 'common/services/http-polling-stopper';
import { handlePostSlackAuth } from 'common/services/slack.service';
import { omitFromQs, getCurrentIteration } from 'common/utils/utils';
import { useEffect } from 'react';
import * as TrackingService from 'common/services/tracking.service';
import { LOCAL_STORAGE_KEYS, TRACKING } from 'common/constants/constants';
import BrowserStorageAccessor from 'common/utils/BrowserStorageAccessor';
import AppRouter from 'common/AppRouter';
import { JOB_NAMES } from 'common/services/jobs-suites.service';
import {
  fetchContributorsMetrics,
  getContributorsMetricsWithHistory
} from '../dashboard.service';
import { useSelector } from 'react-redux';
import { isIterationsPeriodKeySelectedSelector } from 'state/selectors/ui-state';
import { isSelectedTeamParentSelector } from 'state/selectors/teams';
import useMetricsCalculationConfig from '../../../hooks/useMetricsCalculationConfig';

/**
 *
 * @param {Record<string, any>} props Component props
 * @param {*} contributorsOrder
 * @param {*} setContributorsMetrics
 * @param {*} setIsNotificationsLoading
 * @param {*} gitScopeToggle
 */
export function useDashboardEffects(
  props,
  contributorsOrder,
  setContributorsMetrics,
  setIsNotificationsLoading,
  gitScopeToggle
) {
  const localStorageAccessor = BrowserStorageAccessor().local;
  const {
    isRetrospect,
    DateSelectionChanged,
    iterations,
    toggleModal,
    isSyncContributorsCompleted,
    getContributors,
    getTeams,
    isFinishedOnBoarding,
    isVPMode,
    uiState,
    getJobDetails,
    currentTeams
  } = props;
  const { dates, selectedTeamId, selectedIterationId, selectedPeriodKey } =
    uiState;
  const isGroup = useSelector(isSelectedTeamParentSelector);
  const { metricsCalculationStrategy } = useMetricsCalculationConfig();
  const isIterationsPeriodKeySelected = useSelector(
    isIterationsPeriodKeySelectedSelector
  );

  useEffect(() => {
    handlePostSlackAuth({ toggleModal });
    omitFromQs('order_by');
  }, []);

  useEffect(() => {
    if (isSyncContributorsCompleted) {
      getContributors();
      getTeams();
    }
  }, [isSyncContributorsCompleted]);

  useEffect(() => {
    let contributorsMetricsPollingInterval;
    if (isSyncContributorsCompleted && !isFinishedOnBoarding && !isVPMode) {
      contributorsMetricsPollingInterval = setInterval(async () => {
        const contributorsMetrics = await fetchContributorsMetrics({
          dates,
          teamId: selectedTeamId,
          contributorsOrder,
          metricsCalculationStrategy,
          gitScopeToggle
        });
        setContributorsMetrics(contributorsMetrics);
        if (shouldStopOnboardingRecall()) {
          return clearInterval(contributorsMetricsPollingInterval);
        }
      }, 20 * 1000);
    }

    return () => clearInterval(contributorsMetricsPollingInterval);
  }, [isSyncContributorsCompleted, isFinishedOnBoarding, isVPMode]);

  const selectFirstIterationFallback = useDebounceCallback(() => {
    const currentIteration = getCurrentIteration(
      iterations,
      dates.from,
      dates.to,
      selectedIterationId
    );
    // Change the iteration context in case user redirected back to dashboard from other view
    if (!currentIteration && isIterationsPeriodKeySelected) {
      DateSelectionChanged(iterations[0]);
    }
  }, 100);

  useEffect(() => {
    selectFirstIterationFallback();
  }, [dates, selectedPeriodKey]);

  useEffect(() => {
    TrackingService.logEvent(TRACKING.EVENTS.VISIT, {
      page: TRACKING.PAGES.DASHBOARD,
      ...(!isFinishedOnBoarding
        ? { page_tab: TRACKING.PAGE_TABS.INITIAL_PROCESSING }
        : isRetrospect
        ? { page_tab: TRACKING.PAGE_TABS.DASHBOARD_RETRO }
        : {})
    });

    // Log special event for user that gets dashboard after finished processing
    if (isFinishedOnBoarding) {
      TrackingService.logEvent(TRACKING.EVENTS.DASHBOARD_READY);
    }

    localStorageAccessor.set(
      LOCAL_STORAGE_KEYS.LAST_VIEW_MAIN_PAGE,
      AppRouter.PAGE_DASHBOARD.path
    );

    const jobPoolingInterval =
      !isFinishedOnBoarding && !isVPMode
        ? setInterval(async () => {
            const { state: jobState } = await getJobDetails(
              JOB_NAMES.TEAM_SYNC_CONTRIBUTORS
            );
            if (jobState === 'completed') {
              setIsNotificationsLoading(false);
              return clearInterval(jobPoolingInterval);
            }
          }, 5 * 1000)
        : null;
  }, [isRetrospect]);

  const debouncedFetchMetrics = useDebounceCallback(async () => {
    (async () => {
      const contributorsMetrics = await fetchContributorsMetrics({
        dates,
        teamId: selectedTeamId,
        contributorsOrder,
        metricsCalculationStrategy,
        gitScopeToggle
      });
      const metricsWithTeamHistory = await getContributorsMetricsWithHistory(
        contributorsMetrics,
        dates,
        selectedTeamId
      );
      setContributorsMetrics(metricsWithTeamHistory);
    })();
  }, 800);

  useEffect(() => {
    if (!isVPMode && !isGroup) {
      debouncedFetchMetrics();
    }
  }, [
    contributorsOrder,
    dates,
    currentTeams,
    selectedTeamId,
    gitScopeToggle,
    debouncedFetchMetrics
  ]);
}
